import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  Download, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  DollarSign,
  Upload,
  User,
  Layers,
  Zap,
  ChevronRight,
  ChevronDown,
  Rows3,
  HelpCircle,
  Archive,
  ArchiveRestore,
  FilePlus
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getChargebacks, getChargebackStatusCounts, exportChargebacksCsv, runAdjudication, getChargebacksByBatch, downloadChargebackReport, archiveChargeback, unarchiveChargeback, bulkCreateCreditMemos } from '../../config/api';
import StatusHelpModal from '../../components/Chargebacks/StatusHelpModal';
import { useAuth } from '../../contexts/AuthContext';
import EDI844CSVUpload from '../../components/EDI844CSVUpload';
import StatusBadge from '../../components/StatusBadge';

const Chargebacks = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  // Seed the status tab from a ?status= deep-link (e.g. the dashboard's
  // "Action Required" cards) so it lands pre-filtered on the right queue.
  const [filterStatus, setFilterStatus] = useState(() => {
    const s = new URLSearchParams(window.location.search).get('status');
    const valid = ['IMPORTED', 'VALIDATED', 'IN_ERROR', 'APPROVED', 'REJECTED', 'POSTED', 'SUPERSEDED', 'CANCELLED'];
    return s && valid.includes(s) ? s : 'ALL';
  });
  const [filterCustomer, setFilterCustomer] = useState('ALL');
  const [filterDateRange, setFilterDateRange] = useState('ALL');
  const [uploadedFrom, setUploadedFrom] = useState('');
  const [uploadedTo, setUploadedTo] = useState('');
  const [exporting, setExporting] = useState(false);
  const [adjudicating, setAdjudicating] = useState(false);
  const [adjResult, setAdjResult] = useState(null);
  const [viewMode, setViewMode] = useState('claim'); // 'claim' | 'batch'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [sortField, setSortField] = useState('claimDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [showEDI844Upload, setShowEDI844Upload] = useState(false);
  const [showStatusHelp, setShowStatusHelp] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [filterCreditMemo, setFilterCreditMemo] = useState('ALL');
  const [bulkGenerating, setBulkGenerating] = useState(false);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch chargebacks
  const { data: chargebacksData, isLoading, error, refetch } = useQuery({
    queryKey: ['chargebacks', debouncedSearchTerm, filterStatus, filterCustomer, filterDateRange, uploadedFrom, uploadedTo, currentPage, sortField, sortDirection, showArchived, filterCreditMemo],
    queryFn: () => getChargebacks({
      search: debouncedSearchTerm,
      status: filterStatus !== 'ALL' ? filterStatus : undefined,
      customerId: filterCustomer !== 'ALL' ? filterCustomer : undefined,
      dateRange: filterDateRange !== 'ALL' ? filterDateRange : undefined,
      uploadedFrom: uploadedFrom || undefined,
      uploadedTo: uploadedTo || undefined,
      page: currentPage,
      limit: itemsPerPage,
      sortBy: sortField,
      sortOrder: sortDirection,
      includeRejectionDetails: true,
      archived: showArchived || undefined,
      hasCreditMemo: filterCreditMemo !== 'ALL' ? filterCreditMemo : undefined
    }),
    enabled: !!user,
    retry: 3
  });

  const chargebacks = chargebacksData?.chargebacks || [];
  const pagination = chargebacksData?.pagination || { total: 0, pages: 0, page: 1 };

  // Per-status counts for the work-queue tabs
  const { data: statusCounts = {} } = useQuery({
    queryKey: ['chargeback-status-counts'],
    queryFn: getChargebackStatusCounts,
    enabled: !!user
  });

  // Work-queue tabs mapped to ChargebackStatus. Users process each queue in turn.
  const TABS = [
    { key: 'ALL', label: 'All', icon: Layers },
    { key: 'IMPORTED', label: 'New / Imported', icon: Clock },
    { key: 'VALIDATED', label: 'Validated', icon: CheckCircle },
    { key: 'IN_ERROR', label: 'In Error', icon: AlertCircle },
    { key: 'APPROVED', label: 'Approved', icon: CheckCircle },
    { key: 'REJECTED', label: 'Rejected', icon: XCircle },
    { key: 'POSTED', label: 'Posted', icon: DollarSign },
    { key: 'SUPERSEDED', label: 'Superseded', icon: RefreshCw },
    { key: 'CANCELLED', label: 'Cancelled', icon: XCircle }
  ];

  // Derive the most recent processing activity (who + when) for a claim, from its
  // actor/timestamp columns — for the audit/log column in the work queue.
  const lastActivity = useCallback((claim) => {
    const events = [
      claim.rejectedAt && { action: 'Rejected', by: claim.rejectedBy, at: claim.rejectedAt },
      claim.approvedAt && { action: 'Approved', by: claim.approvedBy, at: claim.approvedAt },
      claim.postedAt && { action: 'Posted', by: claim.postedBy, at: claim.postedAt },
      claim.processedAt && { action: 'Processed', by: claim.processedBy, at: claim.processedAt }
    ].filter(Boolean).sort((a, b) => new Date(b.at) - new Date(a.at));
    if (events.length) return events[0];
    return { action: 'Updated', by: claim.processedBy || null, at: claim.updatedAt || claim.createdAt };
  }, []);

  const selectTab = useCallback((key) => {
    setFilterStatus(key);
    setCurrentPage(1);
  }, []);

  // Export the current view (active tab + upload-date range + search) to CSV.
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      await exportChargebacksCsv({
        status: filterStatus,
        search: debouncedSearchTerm,
        uploadedFrom: uploadedFrom || undefined,
        uploadedTo: uploadedTo || undefined
      });
      toast.success('Export downloaded');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to export');
    } finally {
      setExporting(false);
    }
  }, [filterStatus, debouncedSearchTerm, uploadedFrom, uploadedTo]);

  // Download a single claim's CSV report (per-row action).
  const handleRowDownload = useCallback(async (claim) => {
    try {
      const blob = await downloadChargebackReport(claim.id);
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `chargeback-${claim.claimNumber || claim.id}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to download');
    }
  }, []);

  // Archive / restore a claim (removes it from / returns it to the active list).
  const refreshLists = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['chargebacks'] });
    queryClient.invalidateQueries({ queryKey: ['chargeback-status-counts'] });
  }, [queryClient]);

  const handleArchive = useCallback(async (claim) => {
    try {
      await archiveChargeback(claim.id);
      toast.success(`Archived ${claim.claimNumber}`);
      refreshLists();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to archive');
    }
  }, [refreshLists]);

  const handleUnarchive = useCallback(async (claim) => {
    try {
      await unarchiveChargeback(claim.id);
      toast.success(`Restored ${claim.claimNumber}`);
      refreshLists();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to restore');
    }
  }, [refreshLists]);

  // Bulk-generate credit memos for the eligible claims currently shown.
  const handleBulkGenerateMemos = useCallback(async () => {
    const eligible = (chargebacks || []).filter(c => ['VALIDATED', 'APPROVED', 'POSTED'].includes(c.status) && !c.hasCreditMemo);
    if (eligible.length === 0) { toast.error('No eligible claims here (need Validated/Approved/Posted with no existing memo).'); return; }
    if (!window.confirm(`Generate credit memos for ${eligible.length} eligible claim(s) shown? Claims that already have a memo are skipped.`)) return;
    setBulkGenerating(true);
    try {
      const res = await bulkCreateCreditMemos({ claimIds: eligible.map(c => c.id) });
      const d = res?.data || res;
      toast.success(`Generated ${d.createdCount} credit memo(s)${d.skippedCount ? `, skipped ${d.skippedCount}` : ''}`);
      refreshLists();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to generate credit memos');
    } finally { setBulkGenerating(false); }
  }, [chargebacks, refreshLists]);

  // Straight-through auto-adjudication of all pending claims.
  const handleAdjudicate = useCallback(async () => {
    if (!window.confirm('Run auto-adjudication on all pending claims (Imported / Validated / In Error)? Clean claims are approved and issued a draft credit memo; failures are flagged with reason codes.')) return;
    setAdjudicating(true);
    try {
      const res = await runAdjudication({ generateMemos: true });
      setAdjResult(res);
      toast.success(`Adjudicated ${res.processed}: ${res.approved} approved, ${res.flagged} flagged`);
      refetch();
      queryClient.invalidateQueries({ queryKey: ['chargeback-status-counts'] });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Auto-adjudication failed');
    } finally {
      setAdjudicating(false);
    }
  }, [refetch, queryClient]);

  // Debug rejected chargebacks
  useEffect(() => {
    if (chargebacks.length > 0) {
      const rejectedChargebacks = chargebacks.filter(cb => cb.status === 'REJECTED');
      console.log('🚫 Rejected chargebacks from API:', rejectedChargebacks);
      rejectedChargebacks.forEach(chargeback => {
        console.log('🔍 Rejected chargeback details:', {
          id: chargeback.id,
          status: chargeback.status,
          rejectionReasonDetail: chargeback.rejectionReasonDetail,
          rejectionReasonId: chargeback.rejectionReasonId,
          hasRejectionDetail: !!chargeback.rejectionReasonDetail,
          hasRejectionId: !!chargeback.rejectionReasonId,
          rejectionKeys: chargeback.rejectionReasonDetail ? Object.keys(chargeback.rejectionReasonDetail) : 'null'
        });
      });
    }
  }, [chargebacks]);

  // Filtered and sorted chargebacks
  const filteredChargebacks = useMemo(() => {
    let filtered = [...chargebacks];

    // Apply search filter
    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(claim => 
        claim.claimNumber?.toLowerCase().includes(searchLower) ||
        claim.customer?.name?.toLowerCase().includes(searchLower) ||
        claim.contract?.number?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (filterStatus !== 'ALL') {
      filtered = filtered.filter(claim => claim.status === filterStatus);
    }

    // Apply customer filter
    if (filterCustomer !== 'ALL') {
      filtered = filtered.filter(claim => claim.customerId === filterCustomer);
    }

    // Apply date range filter
    if (filterDateRange !== 'ALL') {
      const now = new Date();
      
      switch (filterDateRange) {
        case 'TODAY':
          filtered = filtered.filter(claim => {
            const claimDate = new Date(claim.claimDate);
            return claimDate.toDateString() === now.toDateString();
          });
          break;
        case 'THIS_WEEK':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          filtered = filtered.filter(claim => {
            const claimDate = new Date(claim.claimDate);
            return claimDate >= weekAgo;
          });
          break;
        case 'THIS_MONTH':
          const monthAgo = new Date(now.getFullYear(), now.getMonth(), 1);
          filtered = filtered.filter(claim => {
            const claimDate = new Date(claim.claimDate);
            return claimDate >= monthAgo;
          });
          break;
        case 'THIS_QUARTER':
          const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
          filtered = filtered.filter(claim => {
            const claimDate = new Date(claim.claimDate);
            return claimDate >= quarterStart;
          });
          break;
        case 'THIS_YEAR':
          const yearStart = new Date(now.getFullYear(), 0, 1);
          filtered = filtered.filter(claim => {
            const claimDate = new Date(claim.claimDate);
            return claimDate >= yearStart;
          });
          break;
      }
    }

    return filtered;
  }, [chargebacks, debouncedSearchTerm, filterStatus, filterCustomer, filterDateRange]);

  // Handle sorting
  const handleSort = useCallback((field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField, sortDirection]);

  // Handle page change
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  // Format currency
  const formatCurrency = useCallback((amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }, []);

  // Format date
  const formatDate = useCallback((date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }, []);

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h2 className="text-lg font-semibold text-red-800 mb-2">Error Loading Chargebacks</h2>
          <p className="text-red-600 mb-4">{error.message || 'Failed to load chargeback data'}</p>
          <button
            onClick={() => refetch()}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Chargebacks</h1>
          <p className="text-gray-600">Manage chargeback claims and processing</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw size={20} className={isLoading ? 'animate-spin' : ''} />
          </button>
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden" title="Toggle between individual claims and a per-batch rollup">
            <button onClick={() => setViewMode('claim')} className={`px-3 py-2 text-sm flex items-center gap-1.5 ${viewMode === 'claim' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              <Rows3 size={16} /> By Claim
            </button>
            <button onClick={() => setViewMode('batch')} className={`px-3 py-2 text-sm flex items-center gap-1.5 ${viewMode === 'batch' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              <Layers size={16} /> By Batch
            </button>
          </div>
          <button
            onClick={handleAdjudicate}
            disabled={adjudicating}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-indigo-700 disabled:opacity-50"
            title="Validate, approve clean claims, and flag failures — in one run"
          >
            <Zap size={20} />
            {adjudicating ? 'Adjudicating…' : 'Auto-Adjudicate'}
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-50 disabled:opacity-50"
            title="Export the current view to CSV"
          >
            <Download size={20} />
            {exporting ? 'Exporting…' : 'Export'}
          </button>
          <button
            onClick={handleBulkGenerateMemos}
            disabled={bulkGenerating}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 disabled:opacity-60"
            title="Generate credit memos for eligible claims shown"
          >
            <FilePlus size={20} />
            {bulkGenerating ? 'Generating…' : 'Generate Credit Memos'}
          </button>
          <button
            onClick={() => { setShowArchived(v => !v); setCurrentPage(1); }}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 border ${showArchived ? 'bg-amber-600 text-white border-amber-600 hover:bg-amber-700' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
            title={showArchived ? 'Back to active claims' : 'View archived claims'}
          >
            <Archive size={20} />
            {showArchived ? `Archived (${statusCounts.ARCHIVED ?? 0})` : 'Archived'}
          </button>
          <button
            onClick={() => setShowEDI844Upload(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700"
            title="Upload an EDI 844 chargeback file"
          >
            <Upload size={20} />
            Upload EDI 844 File
          </button>
          <button
            onClick={() => navigate('/app/chargebacks/new')}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary-700"
          >
            <Plus size={20} />
            Add Chargeback
          </button>
        </div>
      </div>

      {viewMode === 'batch' && <BatchRollup navigate={navigate} formatCurrency={formatCurrency} formatDate={formatDate} />}

      {viewMode === 'claim' && (<>
      {/* Work-queue tabs by processing status */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="flex items-center border-b border-gray-200">
          <div className="flex-1 overflow-x-auto">
          <nav className="flex -mb-px min-w-max">
            {TABS.map(({ key, label, icon: Icon }) => {
              const active = filterStatus === key;
              const count = statusCounts[key] ?? 0;
              const s = tabStyle(key);
              return (
                <button
                  key={key}
                  onClick={() => selectTab(key)}
                  className={`flex items-center gap-2 whitespace-nowrap px-5 py-3 border-b-2 text-sm font-medium transition-colors ${
                    active
                      ? `${s.border} ${s.text} ${s.bg}`
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon size={16} className={active ? s.icon : `${s.icon} opacity-50`} />
                  {label}
                  <span className={`ml-1 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-semibold ${s.badge}`}>{count}</span>
                </button>
              );
            })}
          </nav>
          </div>
          <button
            onClick={() => setShowStatusHelp(true)}
            className="flex flex-shrink-0 items-center gap-1.5 border-l border-gray-200 px-3 py-3 text-sm font-medium text-gray-500 hover:text-primary-700 sm:px-4"
            title="What does each status mean?"
          >
            <HelpCircle size={16} /> <span className="hidden sm:inline">Status guide</span>
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search by claim number, customer, or contract..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-3 py-2 border rounded-lg flex items-center gap-2 ${
                  showFilters 
                    ? 'border-primary-500 text-primary-600 bg-primary-50' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter size={16} />
                Filters
              </button>
            </div>
            <div className="text-sm text-gray-500">
              {pagination.total} total chargebacks
            </div>
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="ALL">All Statuses</option>
                  <option value="IMPORTED">Imported</option>
                  <option value="VALIDATED">Validated</option>
                  <option value="IN_ERROR">In Error</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="APPROVED">Approved</option>
                  <option value="POSTED">Posted</option>
                  <option value="SUPERSEDED">Superseded</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Credit Memo</label>
                <select
                  value={filterCreditMemo}
                  onChange={(e) => { setFilterCreditMemo(e.target.value); setCurrentPage(1); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="ALL">All</option>
                  <option value="true">Generated</option>
                  <option value="false">Not generated</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                <select
                  value={filterDateRange}
                  onChange={(e) => setFilterDateRange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="ALL">All Dates</option>
                  <option value="TODAY">Today</option>
                  <option value="THIS_WEEK">This Week</option>
                  <option value="THIS_MONTH">This Month</option>
                  <option value="THIS_QUARTER">This Quarter</option>
                  <option value="THIS_YEAR">This Year</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date Uploaded — From</label>
                <input
                  type="date"
                  value={uploadedFrom}
                  max={uploadedTo || undefined}
                  onChange={(e) => { setUploadedFrom(e.target.value); setCurrentPage(1); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date Uploaded — To</label>
                <input
                  type="date"
                  value={uploadedTo}
                  min={uploadedFrom || undefined}
                  onChange={(e) => { setUploadedTo(e.target.value); setCurrentPage(1); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            {(uploadedFrom || uploadedTo) && (
              <div className="mt-3">
                <button
                  onClick={() => { setUploadedFrom(''); setUploadedTo(''); setCurrentPage(1); }}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Clear upload-date filter
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Chargebacks Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('claimNumber')}
                >
                  <div className="flex items-center gap-1">
                    Claim Number
                    {sortField === 'claimNumber' && (
                      <span className="text-primary-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('customer')}
                >
                  <div className="flex items-center gap-1">
                    Customer
                    {sortField === 'customer' && (
                      <span className="text-primary-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('claimDate')}
                >
                  <div className="flex items-center gap-1">
                    Claim Date
                    {sortField === 'claimDate' && (
                      <span className="text-primary-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('totalAmount')}
                >
                  <div className="flex items-center gap-1">
                    Total Amount
                    {sortField === 'totalAmount' && (
                      <span className="text-primary-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Status
                    {sortField === 'status' && (
                      <span className="text-primary-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Activity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <RefreshCw className="animate-spin h-8 w-8 text-primary-600 mr-3" />
                      <span className="text-gray-600">Loading chargebacks...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredChargebacks.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="text-gray-500">
                      <p className="text-lg font-medium mb-2">No chargebacks found</p>
                      <p>
                        {searchTerm || filterStatus !== 'ALL' || filterDateRange !== 'ALL'
                          ? 'Try adjusting your search or filters'
                          : 'No chargebacks available in the system'
                        }
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredChargebacks.map((claim) => {
                  // Debug logging for rejected chargebacks
                    if (claim.status === 'REJECTED') {
                      console.log('🔍 Chargeback rejection data:', {
                        claimId: claim.id,
                        status: claim.status,
                        rejectionReasonDetail: claim.rejectionReasonDetail,
                        rejectionReasonId: claim.rejectionReasonId,
                        hasRejectionDetail: !!claim.rejectionReasonDetail,
                        hasRejectionId: !!claim.rejectionReasonId,
                        rejectionKeys: claim.rejectionReasonDetail ? Object.keys(claim.rejectionReasonDetail) : 'null',
                        // Check for alternative rejection fields
                        rejectionReason: claim.rejectionReason,
                        rejectionDetails: claim.rejectionDetails,
                        rejectionNotes: claim.rejectionNotes,
                        rejectedBy: claim.rejectedBy,
                        rejectedAt: claim.rejectedAt,
                        validationErrors: claim.validationErrors,
                        allClaimKeys: Object.keys(claim)
                      });
                    }
                  
                  return (
                    <tr key={claim.id} className={`transition-colors ${rowTint(claim.status)}`}>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                          {claim.claimNumber || 'N/A'}
                          {claim.hasCreditMemo && <span className="ml-2 inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700" title="Credit memo generated">CM</span>}
                          <PurposeBadge claim={claim} />
                        </div>
                        <div className="text-sm text-gray-500">
                          Batch:{' '}
                          {(claim.batchId || claim.batch?.id) ? (
                            <button
                              onClick={() => navigate(`/app/batches/${claim.batchId || claim.batch.id}`)}
                              className="font-medium text-primary-600 hover:text-primary-800 hover:underline"
                              title="Open this batch"
                            >
                              {claim.batch?.batchNumber || 'View batch'}
                            </button>
                          ) : (
                            <span>{claim.batch?.batchNumber || 'N/A'}</span>
                          )}
                        </div>
                        {claim.supersedesClaim && claim.status !== 'SUPERSEDED' && claim.status !== 'CANCELLED' && (
                          <div className="text-xs text-amber-700 mt-0.5" title={claim.resubmissionReason || ''}>
                            {claim.purposeCode === '01' ? 'Cancels' : 'Replaces'} {claim.supersedesClaim}
                            {claim.resubmissionReason ? ` — ${claim.resubmissionReason}` : ''}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {claim.customer?.name || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {claim.customer?.type || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{formatDate(claim.claimDate)}</div>
                        <div className="text-xs text-gray-500 mt-0.5">Uploaded: {formatDate(claim.createdAt)}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(claim.totalAmount)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {/* List endpoint returns a line count via _count, not the lines array */}
                          {claim._count?.lines ?? claim.lines?.length ?? 0} line(s)
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge 
                          status={claim.status}
                          subcategory={claim.subcategory}
                          customerType={claim.customer?.type}
                          batchNumber={claim.batch?.batchNumber}
                          claimNumber={claim.claimNumber}
                          rejectionReasonDetail={claim.rejectionReasonDetail}
                          rejectionReasonId={claim.rejectionReasonId}
                          rejectionReason={claim.rejectionReason}
                          rejectionDetails={claim.rejectionDetails}
                          rejectionNotes={claim.rejectionNotes}
                          validationErrors={claim.validationErrors}
                        />
                      </td>
                      <td className="px-6 py-4">
                        {(() => {
                          const a = lastActivity(claim);
                          return (
                            <div className="text-sm">
                              <div className="flex items-center gap-1 text-gray-900">
                                <User size={13} className="text-gray-400" />
                                <span className="font-medium">{a.by || 'system'}</span>
                                <span className="text-gray-400">·</span>
                                <span className="text-gray-600">{a.action}</span>
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {a.at ? new Date(a.at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'N/A'}
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/app/chargebacks/${claim.id}`)}
                            className="text-primary-600 hover:text-primary-900 p-1"
                            title="View Details"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => {
                              console.log('Edit button clicked for chargeback:', claim.id);
                              navigate(`/app/chargebacks/${claim.id}/edit`);
                            }}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title="Edit"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleRowDownload(claim)}
                            className="text-green-600 hover:text-green-900 p-1"
                            title="Download"
                          >
                            <Download size={16} />
                          </button>
                          {showArchived ? (
                            <button
                              onClick={() => handleUnarchive(claim)}
                              className="text-amber-600 hover:text-amber-900 p-1"
                              title="Restore to active claims"
                            >
                              <ArchiveRestore size={16} />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleArchive(claim)}
                              className="text-gray-500 hover:text-gray-800 p-1"
                              title="Archive (hide from the active list)"
                            >
                              <Archive size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="mt-6 flex justify-center">
          <nav className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            {(() => {
              // Sliding window of consecutive page numbers (no duplicates near the ends).
              const windowSize = Math.min(5, pagination.pages);
              const start = Math.max(1, Math.min(currentPage - 2, pagination.pages - windowSize + 1));
              return Array.from({ length: windowSize }, (_, i) => start + i);
            })().map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    page === currentPage
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === pagination.pages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </nav>
        </div>
      )}
      </>)}

      {/* Status guide */}
      <StatusHelpModal open={showStatusHelp} onClose={() => setShowStatusHelp(false)} />

      {/* Auto-adjudication summary */}
      {adjResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Zap size={18} className="text-indigo-600" /> Auto-Adjudication Results</h3>
              <button onClick={() => setAdjResult(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="px-6 py-4 grid grid-cols-4 gap-3">
              {[['Processed', adjResult.processed, 'text-gray-900'], ['Approved', adjResult.approved, 'text-green-600'], ['Flagged', adjResult.flagged, 'text-red-600'], ['Memos', adjResult.memosCreated, 'text-primary-600']].map(([l, v, c]) => (
                <div key={l} className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className={`text-2xl font-bold ${c}`}>{v}</div>
                  <div className="text-xs text-gray-500">{l}</div>
                </div>
              ))}
            </div>
            <div className="px-6 pb-4 overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead><tr className="text-left text-xs text-gray-500 uppercase border-b">
                  <th className="py-2 pr-4">Claim</th><th className="py-2 pr-4">Decision</th><th className="py-2">Reason / Memo</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(adjResult.decisions || []).map((d, i) => (
                    <tr key={i}>
                      <td className="py-2 pr-4 font-mono text-xs text-gray-700">{d.claimNumber}</td>
                      <td className="py-2 pr-4">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${d.decision === 'APPROVED' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}>{d.decision}</span>
                      </td>
                      <td className="py-2 text-xs text-gray-600">{d.decision === 'APPROVED' ? (d.memo ? `Memo ${d.memo}` : '—') : (d.reasons || []).join('; ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end">
              <button onClick={() => setAdjResult(null)} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700">Done</button>
            </div>
          </div>
        </div>
      )}

      {/* EDI 844 File Upload Modal */}
      {showEDI844Upload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <EDI844CSVUpload
            onUploadComplete={(result) => {
              setShowEDI844Upload(false);
              toast.success(`EDI 844 file uploaded successfully! Batch: ${result.data.batchNumber}`);
              refetch();
            }}
            onClose={() => setShowEDI844Upload(false)}
          />
        </div>
      )}
    </div>
  );
};

// Per-batch rollup: one row per EDI 844 upload with a status breakdown; expand
// a row to drill into that batch's individual claims.
const STATUS_PILL = {
  APPROVED: 'bg-green-100 text-green-700',
  POSTED: 'bg-blue-100 text-blue-700',
  REJECTED: 'bg-red-100 text-red-700',
  IN_ERROR: 'bg-orange-100 text-orange-700',
  VALIDATED: 'bg-teal-100 text-teal-700',
  IMPORTED: 'bg-gray-100 text-gray-600',
  SUPERSEDED: 'bg-purple-100 text-purple-700',
  CANCELLED: 'bg-slate-200 text-slate-600'
};

// Row tint + left-border accent by claim status, for at-a-glance scanning.
const ROW_TINT = {
  APPROVED: 'border-l-4 border-green-400 bg-green-50 hover:bg-green-100',
  POSTED: 'border-l-4 border-blue-400 bg-blue-50 hover:bg-blue-100',
  REJECTED: 'border-l-4 border-red-400 bg-red-50 hover:bg-red-100',
  IN_ERROR: 'border-l-4 border-orange-400 bg-orange-50 hover:bg-orange-100',
  VALIDATED: 'border-l-4 border-teal-400 bg-teal-50 hover:bg-teal-100',
  IMPORTED: 'border-l-4 border-gray-300 bg-white hover:bg-gray-50',
  SUPERSEDED: 'border-l-4 border-purple-400 bg-purple-50 hover:bg-purple-100',
  CANCELLED: 'border-l-4 border-slate-300 bg-slate-50 hover:bg-slate-100'
};
const rowTint = (status) => ROW_TINT[status] || 'hover:bg-gray-50';

// Filter-tab styling per status, matching ROW_TINT so the work-queue tabs carry
// the same color code as the rows. Full static class strings (Tailwind-purge safe).
const TAB_STYLE = {
  ALL:        { border: 'border-primary-500', text: 'text-primary-700', bg: 'bg-primary-50', icon: 'text-primary-500', badge: 'bg-primary-100 text-primary-700' },
  IMPORTED:   { border: 'border-gray-400',    text: 'text-gray-700',    bg: 'bg-gray-50',    icon: 'text-gray-400',    badge: 'bg-gray-100 text-gray-600' },
  VALIDATED:  { border: 'border-teal-500',    text: 'text-teal-700',    bg: 'bg-teal-50',    icon: 'text-teal-500',    badge: 'bg-teal-100 text-teal-700' },
  IN_ERROR:   { border: 'border-orange-500',  text: 'text-orange-700',  bg: 'bg-orange-50',  icon: 'text-orange-500',  badge: 'bg-orange-100 text-orange-700' },
  APPROVED:   { border: 'border-green-500',   text: 'text-green-700',   bg: 'bg-green-50',   icon: 'text-green-500',   badge: 'bg-green-100 text-green-700' },
  REJECTED:   { border: 'border-red-500',     text: 'text-red-700',     bg: 'bg-red-50',     icon: 'text-red-500',     badge: 'bg-red-100 text-red-700' },
  POSTED:     { border: 'border-blue-500',    text: 'text-blue-700',    bg: 'bg-blue-50',    icon: 'text-blue-500',    badge: 'bg-blue-100 text-blue-700' },
  SUPERSEDED: { border: 'border-purple-500',  text: 'text-purple-700',  bg: 'bg-purple-50',  icon: 'text-purple-500',  badge: 'bg-purple-100 text-purple-700' },
  CANCELLED:  { border: 'border-slate-400',   text: 'text-slate-700',   bg: 'bg-slate-50',   icon: 'text-slate-500',   badge: 'bg-slate-200 text-slate-600' }
};
const tabStyle = (key) => TAB_STYLE[key] || TAB_STYLE.ALL;

// Badge for the EDI 844 transaction purpose (BEG01): resubmissions and
// cancellations reference a prior claim; originals get no badge.
const PurposeBadge = ({ claim }) => {
  if (!claim) return null;
  if (claim.status === 'SUPERSEDED') {
    return <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-700" title="Replaced by a later resubmission">Superseded</span>;
  }
  if (claim.status === 'CANCELLED') {
    return <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-200 text-slate-600" title="Cancelled by an EDI 844 cancellation">Cancelled</span>;
  }
  if (claim.isResubmission) {
    const label = { '04': 'Change', '05': 'Replace', '6R': 'Resubmit' }[claim.purposeCode] || 'Resubmission';
    return <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800" title={claim.resubmissionReason || 'EDI 844 resubmission'}>↻ {label}</span>;
  }
  if (claim.purposeCode === '01') {
    return <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-200 text-slate-600" title="EDI 844 cancellation request">Cancellation</span>;
  }
  return null;
};

const BatchRollup = ({ navigate, formatCurrency, formatDate }) => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [claims, setClaims] = useState({}); // batchId -> claims[]
  const [claimsLoading, setClaimsLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const d = await getChargebacksByBatch(); setBatches(d.batches || []); }
    catch (e) { toast.error('Failed to load batch rollup'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (b) => {
    if (expanded === b.id) { setExpanded(null); return; }
    setExpanded(b.id);
    if (!claims[b.id]) {
      setClaimsLoading(true);
      try {
        const data = await getChargebacks({ batchId: b.id, limit: 100 });
        setClaims(prev => ({ ...prev, [b.id]: data?.chargebacks || [] }));
      } catch (e) { toast.error('Failed to load batch claims'); }
      finally { setClaimsLoading(false); }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Batches</h3>
          <p className="text-sm text-gray-500">One row per EDI 844 upload — {batches.length} batches. Click a row to expand its claims.</p>
        </div>
        <button onClick={load} className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"><RefreshCw className="h-4 w-4 mr-2" /> Refresh</button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50"><tr>
            {['', 'Batch', 'Claims', 'Status Breakdown', 'Total Amount', 'Batch Status', 'Uploaded'].map((h, i) => (
              <th key={i} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
            ))}
          </tr></thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-500">Loading…</td></tr>
            ) : batches.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">No batches yet.</td></tr>
            ) : batches.map(b => (
              <React.Fragment key={b.id}>
                <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggle(b)}>
                  <td className="px-4 py-3 text-gray-400">{expanded === b.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{b.batchNumber}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{b.claimCount}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(b.statusCounts || {}).map(([s, n]) => (
                        <span key={s} className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_PILL[s] || 'bg-gray-100 text-gray-600'}`}>{s.replace('_', ' ')} {n}</span>
                      ))}
                      {Object.keys(b.statusCounts || {}).length === 0 && <span className="text-gray-400">—</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{formatCurrency(b.totalAmount)}</td>
                  <td className="px-4 py-3 text-sm"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_PILL[b.batchStatus] || 'bg-gray-100 text-gray-600'}`}>{b.batchStatus}</span></td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatDate(b.uploadedAt)}</td>
                </tr>
                {expanded === b.id && (
                  <tr><td colSpan={7} className="bg-gray-50 px-6 py-3">
                    {claimsLoading && !claims[b.id] ? (
                      <p className="text-sm text-gray-500">Loading claims…</p>
                    ) : (
                      <table className="min-w-full text-sm">
                        <thead><tr className="text-left text-xs text-gray-500 uppercase">
                          <th className="py-2 pr-4">Claim</th><th className="py-2 pr-4">Customer</th><th className="py-2 pr-4">Amount</th><th className="py-2 pr-4">Status</th><th className="py-2"></th>
                        </tr></thead>
                        <tbody className="divide-y divide-gray-100">
                          {(claims[b.id] || []).map(c => (
                            <tr key={c.id}>
                              <td className="py-2 pr-4 font-mono text-xs text-gray-700">{c.claimNumber}</td>
                              <td className="py-2 pr-4 text-gray-600">{c.customer?.name || '—'}</td>
                              <td className="py-2 pr-4 text-gray-900">{formatCurrency(c.totalAmount)}</td>
                              <td className="py-2 pr-4"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_PILL[c.status] || 'bg-gray-100 text-gray-600'}`}>{c.status}</span></td>
                              <td className="py-2"><button onClick={(e) => { e.stopPropagation(); navigate(`/app/chargebacks/${c.id}`); }} className="text-primary-600 hover:text-primary-800 text-xs">View</button></td>
                            </tr>
                          ))}
                          {(claims[b.id] || []).length === 0 && <tr><td colSpan={5} className="py-3 text-gray-400">No claims in this batch.</td></tr>}
                        </tbody>
                      </table>
                    )}
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Chargebacks;
